import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { ArrowRight } from "lucide-react"

interface CTASectionProps {
  title: string
  description?: string
  primaryAction: {
    label: string
    href: string
  }
  secondaryAction?: {
    label: string
    href: string
  }
  variant?: "default" | "centered" | "split"
  background?: "default" | "muted" | "primary"
}

export function CTASection({
  title,
  description,
  primaryAction,
  secondaryAction,
  variant = "centered",
  background = "muted"
}: CTASectionProps) {
  const bgClass = {
    default: "bg-background",
    muted: "bg-muted/50",
    primary: "bg-primary text-primary-foreground"
  }[background]

  const isPrimary = background === "primary"

  if (variant === "split") {
    return (
      <section className={`py-16 sm:py-24 ${bgClass}`}>
        <div className="container px-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                {title}
              </h2>
              {description && (
                <p className={`mt-4 text-lg ${isPrimary ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  {description}
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                variant={isPrimary ? "secondary" : "default"}
                asChild
              >
                <Link to={primaryAction.href}>
                  {primaryAction.label}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              {secondaryAction && (
                <Button
                  size="lg"
                  variant={isPrimary ? "outline" : "outline"}
                  className={isPrimary ? "border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10" : ""}
                  asChild
                >
                  <Link to={secondaryAction.href}>
                    {secondaryAction.label}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className={`py-16 sm:py-24 ${bgClass}`}>
      <div className="container px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {title}
          </h2>
          {description && (
            <p className={`mt-4 text-lg ${isPrimary ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
              {description}
            </p>
          )}
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Button
              size="lg"
              variant={isPrimary ? "secondary" : "default"}
              asChild
            >
              <Link to={primaryAction.href}>
                {primaryAction.label}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            {secondaryAction && (
              <Button
                size="lg"
                variant={isPrimary ? "outline" : "outline"}
                className={isPrimary ? "border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10" : ""}
                asChild
              >
                <Link to={secondaryAction.href}>
                  {secondaryAction.label}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
